/**
 * MeasurementsStep — "Medições" step of the RiskApplicationModal stepper.
 *
 * Editable table of measured values (history) for the risk application,
 * controlled by the parent form via useFieldArray — mirrors the wiring
 * pattern of ControlMeasuresStep (#221).
 */

import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { NR16_ANNEXES } from '../../../types/environmentalMeasurement';
import {
  UNHEALTHY_DEGREES,
  OCC_UNHEALTHY_DEGREE_LABELS,
  type UnhealthyDegree,
} from '../../../types/occupationalRisk';
import type { StepperFormValues } from '../RiskApplicationModal';

export interface MeasurementEquipmentOption {
  id: number;
  name: string;
  serialNumber?: string | null;
  /** true quando o equipamento foi selecionado antes mas não está mais ativo (soft-deletado). */
  orphaned?: boolean;
}

interface MeasurementsStepProps {
  equipmentOptions: MeasurementEquipmentOption[];
  defaultUnit: string;
  disabled: boolean;
  /** Risco de periculosidade (NR-16): a linha ganha a área de enquadramento (#424). */
  hazardous?: boolean;
  /** Risco de insalubridade (NR-15): a linha ganha o enquadramento e o grau (#357). */
  unhealthy?: boolean;
  /** Grau do catálogo: sugerido ao marcar o enquadramento, nunca imposto (#357). */
  defaultUnhealthyDegree?: string;
}

/**
 * O grau no catálogo de agentes é texto sem validação — a planilha do cliente chega a trazer
 * "0.2" —, e sugerir um valor desses deixaria a linha com algo que o servidor recusa.
 */
const grauSugerido = (grauDoCatalogo: string): string =>
  UNHEALTHY_DEGREES.includes(grauDoCatalogo as UnhealthyDegree) ? grauDoCatalogo : '';

const NOVA_LINHA = {
  measurementEquipmentId: null,
  value: null,
  unit: '',
  measuredAt: null,
  observations: '',
  storedQuantity: null,
  storedQuantityUnit: '',
  transportedQuantity: null,
  transportedQuantityUnit: '',
  riskArea: '',
  storageType: '',
  hazardousFraming: false,
  hazardousAnnex: '',
  unhealthyFraming: false,
  unhealthyDegreeApplied: '',
};

export const MeasurementsStep: React.FC<MeasurementsStepProps> = ({
  equipmentOptions,
  defaultUnit,
  disabled,
  hazardous = false,
  unhealthy = false,
  defaultUnhealthyDegree = '',
}) => {
  const { control, setValue, watch } = useFormContext<StepperFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'measurements' });
  const [abertas, setAbertas] = React.useState<Record<string, boolean>>({});

  const alternarLinha = (id: string) => setAbertas((atual) => ({ ...atual, [id]: !atual[id] }));

  // A tabela já tem cinco colunas: o que é da NR-16 e da NR-15 abre por linha, e o rótulo do
  // botão nomeia o que há ali dentro.
  const detalheDaLinha = [hazardous && 'periculosidade', unhealthy && 'insalubridade']
    .filter(Boolean)
    .join(' e ');

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">Medições registradas</Typography>
        <Button
          type="button"
          size="small"
          startIcon={<AddIcon />}
          disabled={disabled}
          onClick={() => append({ ...NOVA_LINHA, unit: defaultUnit })}
        >
          Adicionar medição
        </Button>
      </Stack>

      {fields.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nenhuma medição registrada.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Aparelho</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Unidade</TableCell>
              <TableCell>Observações</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, i) => (
              <React.Fragment key={field.id}>
              <TableRow>
                <TableCell>
                  <Controller
                    name={`measurements.${i}.measuredAt`}
                    control={control}
                    render={({ field: f, fieldState }) => (
                      <DatePicker
                        value={f.value}
                        onChange={f.onChange}
                        disabled={disabled}
                        slotProps={{ textField: { size: 'small', error: !!fieldState.error, helperText: fieldState.error?.message } }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Controller
                    name={`measurements.${i}.measurementEquipmentId`}
                    control={control}
                    render={({ field: f }) => (
                      <Autocomplete
                        options={equipmentOptions}
                        disabled={disabled}
                        getOptionLabel={(o) => `${o.name}${o.serialNumber ? ` (${o.serialNumber})` : ''}${o.orphaned ? ' (excluído)' : ''}`}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={equipmentOptions.find((o) => o.id === f.value) ?? null}
                        onChange={(_, v) => f.onChange(v ? v.id : null)}
                        renderInput={(p) => <TextField {...p} size="small" sx={{ minWidth: 180 }} />}
                      />
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Controller
                    name={`measurements.${i}.value`}
                    control={control}
                    render={({ field: f, fieldState }) => (
                      <TextField
                        type="number"
                        size="small"
                        disabled={disabled}
                        sx={{ width: 100 }}
                        value={f.value ?? ''}
                        onChange={(e) => f.onChange(e.target.value === '' ? null : Number(e.target.value))}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        inputProps={{ 'aria-label': 'Valor' }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Controller
                    name={`measurements.${i}.unit`}
                    control={control}
                    render={({ field: f }) => (
                      <TextField size="small" disabled={disabled} sx={{ width: 90 }} value={f.value ?? ''} onChange={f.onChange} inputProps={{ 'aria-label': 'Unidade' }} />
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Controller
                    name={`measurements.${i}.observations`}
                    control={control}
                    render={({ field: f }) => (
                      <TextField size="small" disabled={disabled} value={f.value ?? ''} onChange={f.onChange} inputProps={{ 'aria-label': 'Observações' }} />
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {detalheDaLinha && (
                      <IconButton
                        size="small"
                        onClick={() => alternarLinha(field.id)}
                        aria-label={`${abertas[field.id] ? 'fechar' : 'abrir'} ${detalheDaLinha}`}
                      >
                        {abertas[field.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    )}
                    <IconButton size="small" disabled={disabled} onClick={() => remove(i)} aria-label="remover">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
              {detalheDaLinha && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, borderBottom: 0 }}>
                    <Collapse in={Boolean(abertas[field.id])} unmountOnExit>
                      <Box sx={{ py: 2 }}>
                        {hazardous && (
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Periculosidade (NR-16)
                        </Typography>
                        )}
                        {hazardous && (
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                              name={`measurements.${i}.storedQuantity`}
                              control={control}
                              render={({ field: f, fieldState }) => (
                                <TextField
                                  type="number"
                                  label="Quantidade armazenada"
                                  size="small"
                                  fullWidth
                                  disabled={disabled}
                                  value={f.value ?? ''}
                                  onChange={(e) => f.onChange(e.target.value === '' ? null : Number(e.target.value))}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  inputProps={{ 'aria-label': 'Quantidade armazenada', step: 'any' }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, md: 2 }}>
                            <Controller
                              name={`measurements.${i}.storedQuantityUnit`}
                              control={control}
                              render={({ field: f }) => (
                                <TextField
                                  label="Unidade"
                                  size="small"
                                  fullWidth
                                  disabled={disabled}
                                  value={f.value ?? ''}
                                  onChange={f.onChange}
                                  inputProps={{ 'aria-label': 'Unidade da quantidade armazenada' }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                              name={`measurements.${i}.transportedQuantity`}
                              control={control}
                              render={({ field: f, fieldState }) => (
                                <TextField
                                  type="number"
                                  label="Quantidade transportada"
                                  size="small"
                                  fullWidth
                                  disabled={disabled}
                                  value={f.value ?? ''}
                                  onChange={(e) => f.onChange(e.target.value === '' ? null : Number(e.target.value))}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  inputProps={{ 'aria-label': 'Quantidade transportada', step: 'any' }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, md: 2 }}>
                            <Controller
                              name={`measurements.${i}.transportedQuantityUnit`}
                              control={control}
                              render={({ field: f }) => (
                                <TextField
                                  label="Unidade"
                                  size="small"
                                  fullWidth
                                  disabled={disabled}
                                  value={f.value ?? ''}
                                  onChange={f.onChange}
                                  inputProps={{ 'aria-label': 'Unidade da quantidade transportada' }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                              name={`measurements.${i}.riskArea`}
                              control={control}
                              render={({ field: f }) => (
                                <TextField
                                  label="Área de risco"
                                  size="small"
                                  fullWidth
                                  disabled={disabled}
                                  value={f.value ?? ''}
                                  onChange={f.onChange}
                                  inputProps={{ 'aria-label': 'Área de risco' }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                              name={`measurements.${i}.storageType`}
                              control={control}
                              render={({ field: f }) => (
                                <TextField
                                  label="Tipo de armazenamento"
                                  size="small"
                                  fullWidth
                                  disabled={disabled}
                                  value={f.value ?? ''}
                                  onChange={f.onChange}
                                  inputProps={{ 'aria-label': 'Tipo de armazenamento' }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                              name={`measurements.${i}.hazardousFraming`}
                              control={control}
                              render={({ field: f, fieldState }) => (
                                <>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={Boolean(f.value)}
                                        disabled={disabled}
                                        onChange={(e) => {
                                          f.onChange(e.target.checked);
                                          if (!e.target.checked) {
                                            setValue(`measurements.${i}.hazardousAnnex`, '');
                                          }
                                        }}
                                      />
                                    }
                                    label="A atividade está descrita no anexo da NR-16"
                                  />
                                  {fieldState.error && (
                                    <Typography variant="caption" color="error" display="block">
                                      {fieldState.error.message}
                                    </Typography>
                                  )}
                                </>
                              )}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                              name={`measurements.${i}.hazardousAnnex`}
                              control={control}
                              render={({ field: f, fieldState }) => (
                                <TextField
                                  select
                                  label="Anexo da NR-16"
                                  size="small"
                                  fullWidth
                                  disabled={disabled || !watch(`measurements.${i}.hazardousFraming`)}
                                  value={f.value ?? ''}
                                  onChange={f.onChange}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                >
                                  <MenuItem value="">Não informado</MenuItem>
                                  {NR16_ANNEXES.map((anexo) => (
                                    <MenuItem key={anexo} value={anexo}>
                                      {anexo}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              )}
                            />
                          </Grid>
                        </Grid>
                        )}
                        {unhealthy && (
                        <Typography variant="subtitle2" sx={{ mb: 1, mt: hazardous ? 3 : 0 }}>
                          Insalubridade (NR-15)
                        </Typography>
                        )}
                        {unhealthy && (
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                              name={`measurements.${i}.unhealthyFraming`}
                              control={control}
                              render={({ field: f, fieldState }) => (
                                <>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={Boolean(f.value)}
                                        disabled={disabled}
                                        onChange={(e) => {
                                          f.onChange(e.target.checked);
                                          // Grau sem enquadramento é par incompleto, e o servidor
                                          // o recusa; ao marcar, o catálogo entra como sugestão.
                                          setValue(
                                            `measurements.${i}.unhealthyDegreeApplied`,
                                            e.target.checked ? grauSugerido(defaultUnhealthyDegree) : '',
                                          );
                                        }}
                                      />
                                    }
                                    label="A atividade está enquadrada como insalubre (NR-15)"
                                  />
                                  {fieldState.error && (
                                    <Typography variant="caption" color="error" display="block">
                                      {fieldState.error.message}
                                    </Typography>
                                  )}
                                </>
                              )}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                              name={`measurements.${i}.unhealthyDegreeApplied`}
                              control={control}
                              render={({ field: f, fieldState }) => (
                                <TextField
                                  select
                                  name={f.name}
                                  label="Grau de insalubridade aplicado"
                                  size="small"
                                  fullWidth
                                  disabled={disabled || !watch(`measurements.${i}.unhealthyFraming`)}
                                  value={f.value ?? ''}
                                  onChange={f.onChange}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                >
                                  <MenuItem value="">Não informado</MenuItem>
                                  {UNHEALTHY_DEGREES.map((grau) => (
                                    <MenuItem key={grau} value={grau}>
                                      {OCC_UNHEALTHY_DEGREE_LABELS[grau]}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              )}
                            />
                          </Grid>
                        </Grid>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default MeasurementsStep;
